import { useFormik, type FormikConfig, type FormikValues } from 'formik'
import { useEffect, useRef, useCallback, useMemo } from 'react'
import { useSnackbar } from 'notistack'
import {
  formPersistence,
  FormPersistence,
  type FormDraft
} from '../services/offline/formPersistence'

export interface UsePersistedFormikOptions {
  /**
   * Auto-save interval in milliseconds
   * @default 5000 (5 seconds)
   */
  autoSaveInterval?: number

  /**
   * Clear saved data on successful submit
   * @default true
   */
  clearOnSubmit?: boolean

  /**
   * Show notifications for auto-save actions
   * @default false
   */
  showNotifications?: boolean

  /**
   * Form type for categorization
   */
  formType: FormDraft['formType']

  /**
   * Protocol ID for organization
   */
  protocolId: number

  /**
   * Additional identifier for unique forms of same type
   */
  additionalId?: string | number
}

export interface PersistedFormikResult<T extends FormikValues>
  extends ReturnType<typeof useFormik<T>> {
  /**
   * Manually save form data
   */
  saveFormData: () => void

  /**
   * Clear saved form data
   */
  clearSavedData: () => void

  /**
   * Check if there's saved data different from current values
   */
  hasUnsavedChanges: boolean

  /**
   * Restore from saved data
   */
  restoreFromSaved: () => void

  /**
   * Get form persistence key
   */
  persistKey: string

  /**
   * Last auto-save timestamp
   */
  lastSaved: Date | null
}

export function usePersistedFormik<T extends FormikValues>(
  config: FormikConfig<T>,
  options: UsePersistedFormikOptions
): PersistedFormikResult<T> {
  const {
    autoSaveInterval = 5000,
    clearOnSubmit = true,
    showNotifications = false,
    formType,
    protocolId,
    additionalId
  } = options

  const { enqueueSnackbar } = useSnackbar()
  const autoSaveIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedRef = useRef<Date | null>(null)
  const isSubmittingRef = useRef(false)

  // Generate unique persistence key
  const persistKey = FormPersistence.generateFormKey(formType, protocolId, additionalId)

  // Load saved data and merge with initial values
  const savedData = formPersistence.load(persistKey)
  const initialValues = savedData ? { ...config.initialValues, ...savedData } : config.initialValues

  // Create formik instance
  const formik = useFormik<T>({
    ...config,
    initialValues,
    onSubmit: async (values, formikHelpers) => {
      isSubmittingRef.current = true

      try {
        await config.onSubmit(values, formikHelpers)

        // Clear saved data on successful submit if enabled
        if (clearOnSubmit) {
          formPersistence.clear(persistKey)
          lastSavedRef.current = null

          if (showNotifications) {
            enqueueSnackbar('Formularz wysłany - kopia robocza usunięta', {
              variant: 'success',
              autoHideDuration: 3000
            })
          }
        }
      } finally {
        isSubmittingRef.current = false
      }
    }
  })

  // Manual save function
  const saveFormData = useCallback(() => {
    // Only save if form has actual changes (dirty) and user has made meaningful input
    if (formik.dirty && !isSubmittingRef.current) {
      // Check if values are different from initial values (not just touched)
      const hasRealChanges = JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)

      if (hasRealChanges) {
        formPersistence.save(persistKey, formik.values, formType, protocolId)
        lastSavedRef.current = new Date()

        if (showNotifications) {
          enqueueSnackbar('Kopia robocza zapisana', {
            variant: 'info',
            autoHideDuration: 2000
          })
        }
      }
    }
  }, [
    formik.values,
    formik.dirty,
    formik.initialValues,
    persistKey,
    formType,
    protocolId,
    showNotifications,
    enqueueSnackbar
  ])

  // Clear saved data function
  const clearSavedData = useCallback(() => {
    formPersistence.clear(persistKey)
    lastSavedRef.current = null

    if (showNotifications) {
      enqueueSnackbar('Kopia robocza usunięta', {
        variant: 'info',
        autoHideDuration: 2000
      })
    }
  }, [persistKey, showNotifications, enqueueSnackbar])

  // Discard local changes and restore original server data
  const restoreFromSaved = useCallback(() => {
    // Clear any saved draft
    formPersistence.clear(persistKey)

    // Reset form to original server values (without any saved draft)
    formik.setValues(config.initialValues)
    formik.setTouched({})

    if (showNotifications) {
      enqueueSnackbar('Odrzucono zmiany lokalne', {
        variant: 'info',
        autoHideDuration: 3000
      })
    }
  }, [persistKey, formik, config.initialValues, showNotifications, enqueueSnackbar])

  // Check for unsaved changes - compare current values with original server data
  const hasUnsavedChanges = useMemo(() => {
    try {
      return JSON.stringify(formik.values) !== JSON.stringify(config.initialValues)
    } catch (error) {
      console.error('Failed to compare form data:', error)
      return false
    }
  }, [formik.values, config.initialValues])

  // Auto-save effect
  useEffect(() => {
    if (autoSaveInterval > 0) {
      // Clear existing interval
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current)
      }

      // Set up new interval
      autoSaveIntervalRef.current = setInterval(() => {
        if (formik.dirty && !isSubmittingRef.current) {
          saveFormData()
        }
      }, autoSaveInterval)

      // Cleanup on unmount
      return () => {
        if (autoSaveIntervalRef.current) {
          clearInterval(autoSaveIntervalRef.current)
        }
      }
    }
  }, [formik.values, formik.dirty, autoSaveInterval, saveFormData])

  // Save on page unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (formik.dirty && !isSubmittingRef.current) {
        const hasRealChanges =
          JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
        if (hasRealChanges) {
          formPersistence.save(persistKey, formik.values, formType, protocolId)
        }
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      // Final save on cleanup
      if (formik.dirty && !isSubmittingRef.current) {
        const hasRealChanges =
          JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
        if (hasRealChanges) {
          formPersistence.save(persistKey, formik.values, formType, protocolId)
        }
      }
    }
  }, [formik.values, formik.dirty, formik.initialValues, persistKey, formType, protocolId])

  // Cleanup old drafts on mount
  useEffect(() => {
    formPersistence.clearOldDrafts()
  }, [])

  return {
    ...formik,
    saveFormData,
    clearSavedData,
    hasUnsavedChanges,
    restoreFromSaved,
    persistKey,
    lastSaved: lastSavedRef.current
  } as PersistedFormikResult<T>
}
